/**
 * The `Enquiry Type` master — what the SERVER actually offers, not what our enum guesses.
 *
 * Our `ENQUIRY_TYPES` const lists `Technical | Provision | Commercial | Bonded Stores`, taken from
 * the prototype. The live doctype holds three, and they do not match:
 *
 *     Provision · Technical · Service          (queried 2026-09-03)
 *
 * `Commercial` and `Bonded Stores` do not exist on the server, and `Service` is not in our enum.
 * Offering the enum in a picker would let a purchaser choose a type the server will reject, which
 * is why this reads the master instead — the same reasoning every other picker in this codebase
 * follows.
 *
 * It matters more than it looks: the enquiry type scopes item search (`items.get_items`'s
 * `enquiry_type`), so choosing the wrong one at create time narrows the catalogue for the whole
 * enquiry. Provision resolves to a real provision catalogue; the others are barely mapped (`D21`).
 */

import { useQuery } from '@tanstack/react-query'
import { getResource } from './frappe'
import { str } from './wire'

export function useEnquiryTypes() {
  return useQuery({
    queryKey: ['picker', 'Enquiry Type'] as const,
    queryFn: async (): Promise<string[]> => {
      // Literal, not a const: `server/allowlistScan.ts` resolves doctype names at the call site,
      // and a const here would need a hand-maintained exemption instead. See its header.
      const page = await getResource<Record<string, unknown>>('Enquiry Type', {
        fields: ['name'],
        limit: 50,
        orderBy: 'name asc',
      })
      return page.data.map((row) => str(row.name)).filter((name) => name !== '')
    },
    // A master of three rows that changes about never.
    staleTime: 30 * 60_000,
  })
}
